import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import isEmail from "validator/lib/isEmail";

import Loading from "../common/components/Loading";
import { pathToLogoComplete } from "../common/assets";
import { backgrounds } from "../theme/theme";

interface LoginFormProps {
  isLoginFailed: boolean;
  isSubmittingLoginForm: boolean;
  onSubmit: (email: string, password: string) => void;
}

function LoginForm(props: LoginFormProps) {
  const { isLoginFailed, isSubmittingLoginForm, onSubmit } = props;

  const [data, setData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = { email: "" };

    if (!isEmail(data.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);

    if (newErrors.email.length === 0) {
      onSubmit(data.email, data.password);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img
              src={pathToLogoComplete}
              alt="DoneStreet"
              style={{
                width: 150,
              }}
            />
          </Box>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Login
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sign in to your account
          </Typography>
        </Box>

        {isLoginFailed && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Incorrect email address or password
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            id="email"
            name="email"
            label="Email Address"
            type="email"
            autoComplete="email"
            autoFocus
            value={data.email}
            onChange={onChange}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="password"
            name="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            value={data.password}
            onChange={onChange}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {isSubmittingLoginForm ? <Loading /> : "Login"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default LoginForm;
